import { axios } from '@/lib/axios';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import queryClient from '@/lib/react-query';
import { CallCenterDashboardResponse, featureKeys, featurePath, HeartbeatResponse } from '.';

// Heartbeat returns a TOP-LEVEL `{ feed_paused, server_time }` body -- there is
// no `{ data }` envelope for the shared `post()` helper in `@/utils/api` to
// unwrap. `post()` unconditionally returns `body.data`, which would resolve to
// `undefined` here. Call axios directly (the response interceptor has already
// stripped the raw AxiosResponse down to the JSON body) and fall back to that
// body when there's no `.data` envelope to peel off.
export const postHeartbeat = async (): Promise<HeartbeatResponse> => {
  const body = await axios.post<HeartbeatResponse>(`${featurePath}/heartbeat`, undefined, {
    skipErrorToast: true,
  });
  return (body as any)?.data ?? (body as unknown as HeartbeatResponse);
};

// Controller decision: the response's `server_time` is ISO-8601 with a UTC
// offset, while `agent.last_heartbeat_at` on the dashboard cache is a
// Phoenix-naive datetime string. Writing the former into the latter would mix
// formats and corrupt every date helper that reads `last_heartbeat_at`, so
// only `feed_paused` is reconciled here -- the next dashboard poll/refetch is
// what updates `last_heartbeat_at`.
export const useSendHeartbeat = () => {
  return useMutation<HeartbeatResponse, AxiosError, void>({
    mutationFn: postHeartbeat,
    onSuccess: (response) => {
      queryClient.setQueryData<CallCenterDashboardResponse>(featureKeys.dashboard(), (prev) =>
        prev ? { ...prev, agent: { ...prev.agent, feed_paused: response.feed_paused } } : prev,
      );
    },
  });
};
